/**
 * PostHog product analytics for the React web app.
 *
 * Mirrors the no-op pattern of sentry.config.js: completely inert when
 * `REACT_APP_POSTHOG_KEY` is not set, so this can ship before the key
 * exists. Set the key (+ optional host) in `frontend/.env` and rebuild —
 * no code changes needed (CRA inlines REACT_APP_* at build time).
 *
 * Setup:
 *   1. PostHog → Project Settings → copy the Project API Key (phc_...).
 *   2. Set in `frontend/.env`:
 *        REACT_APP_POSTHOG_KEY="phc_..."
 *        REACT_APP_POSTHOG_HOST="https://us.i.posthog.com"   # EU: https://eu.i.posthog.com
 *   3. Rebuild the frontend.
 *
 * We capture a deliberate conversion funnel: `signup` -> `draft` -> `ad-free`.
 * Autocapture + session replay are OFF and a before_send hook strips PII so
 * only the explicit events + pageviews we choose ever leave the browser.
 */
import posthog from 'posthog-js';

const key = process.env.REACT_APP_POSTHOG_KEY;
const host = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let enabled = false;

if (key) {
  posthog.init(key, {
    api_host: host,
    // When `host` is a reverse proxy (e.g. https://fantasyworldstar.com/ingest),
    // ui_host keeps toolbar/app links pointing at the real PostHog UI.
    ui_host: 'https://us.posthog.com',
    // Only build person profiles once we identify() an authenticated user.
    person_profiles: 'identified_only',
    // Core Web Vitals (LCP/CLS/INP/FCP) → $web_vitals events (project opt-in is also ON).
    capture_performance: { web_vitals: true },
    // We fire our own explicit funnel events; no DOM autocapture (less PII risk).
    autocapture: false,
    // Keep SPA pageviews (history-based) + page-leave for retention/traffic.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    // No session recording — this app shows user-typed content we don't want captured.
    disable_session_recording: true,
    // Defense-in-depth: drop common PII keys before any event is sent.
    before_send: (event) => {
      if (event && event.properties) {
        delete event.properties.email;
        delete event.properties.$email;
        delete event.properties.name;
        delete event.properties.full_name;
      }
      return event;
    },
  });
  enabled = true;
  // Expose the instance for debugging/console use (the phc_ key is public).
  if (typeof window !== 'undefined') window.posthog = posthog;
  // eslint-disable-next-line no-console
  console.info('[PostHog] initialized · host=' + host);
} else {
  // eslint-disable-next-line no-console
  console.info('[PostHog] disabled (REACT_APP_POSTHOG_KEY not set).');
}

/** Link all subsequent events to a stable user id. Call after auth succeeds. */
export function posthogIdentify(userId, props) {
  if (!enabled || !userId) return;
  try { posthog.identify(String(userId), props || {}); } catch { /* never break auth */ }
}

/** Unlink the device from the prior user. Call on logout. */
export function posthogReset() {
  if (!enabled) return;
  try { posthog.reset(); } catch { /* ignore */ }
}

/** Capture a funnel/product event. No-op when PostHog is disabled. */
export function posthogCapture(eventName, props) {
  if (!enabled) return;
  try { posthog.capture(eventName, props || {}); } catch { /* never break the UI */ }
}

export { posthog };
